// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EventMap = Record<string, any>;
type EventKey<T extends EventMap> = string & keyof T;

export type EventReceiver<T> = (params: T) => void;

export class EventEmitter<T extends EventMap> {
  private readonly events: Map<EventKey<T>, Set<(p: EventMap[keyof EventMap]) => void>> = new Map();

  emit<K extends EventKey<T>>(eventName: K, params: T[K]): void {
    const handlers = this.getEventHandlers<K>(eventName);

    handlers.forEach((handler) => {
      handler(params);
    });
  }

  addEventListener<K extends EventKey<T>>(event: K, handler: EventReceiver<T[K]>): VoidFunction {
    const handlers = this.getEventHandlers<K>(event);
    handlers.add(handler);

    return () => {
      this.removeEventListener(event, handler);
    };
  }

  removeEventListener<K extends EventKey<T>>(event: K, handler: EventReceiver<T[K]>): void {
    const handlers = this.getEventHandlers<K>(event);

    if (handlers.size === 0) {
      handlers.clear();
      return;
    }

    handlers.delete(handler);
  }

  private getEventHandlers<K extends EventKey<T>>(event: K): Set<(p: EventMap[keyof EventMap]) => void> {
    let set = this.events.get(event);

    if (!set) {
      set = new Set<(p: EventMap[keyof EventMap]) => void>();
      this.events.set(event, set);
    }

    return set;
  }
}
